/**
 * v1.4.45 — Screen Size setup dialog.
 *
 * Opens as a full-screen overlay after first-time activation (see
 * Activation.jsx setting `tv_player.firstRunScreenSize`) so the user
 * can adjust the app's overall size for their TV BEFORE landing on
 * the main menu. Also embeddable from Settings for later use via the
 * `show={true}` prop.
 *
 * The slider drives CSS `zoom` on `<html>` — layout REFLOWS
 * accordingly so click targets, focus rings, scrolling all stay
 * aligned. The four preset "chip" buttons cover the common cases.
 */
import React, { useEffect, useState } from "react";
import {
  getUiScale,
  setUiScale,
  isUiScaleEnabled,
  setUiScaleEnabled,
  bestFitScale,
  UI_SCALE_MIN,
  UI_SCALE_MAX,
  UI_SCALE_DEFAULT,
} from "../lib/uiScale";
import { Minus, Plus, RotateCcw, Check, Maximize2 } from "lucide-react";

const PRESETS = [
  { value: 0.8, label: "80%", hint: "Fit more on screen" },
  { value: 0.9, label: "90%", hint: "Slightly smaller" },
  { value: 1.0, label: "100%", hint: "Default" },
  { value: 1.1, label: "110%", hint: "Slightly bigger" },
  { value: 1.2, label: "120%", hint: "TV / far viewing" },
  { value: 1.3, label: "130%", hint: "Extra large" },
];

export default function ScreenSizeSetup({
  onDone,
  showFrame = true,
  firstRun = false,
}) {
  const [scale, setScale] = useState(() => getUiScale());
  // v1.4.46 — Custom scaling is OPT-IN. When disabled, the slider
  // still reflects the saved value (so the user can preview it) but
  // the app stays at 100%. Enable is auto-flipped on the moment they
  // touch any control that actually changes size.
  const [enabled, setEnabled] = useState(() => isUiScaleEnabled());

  useEffect(() => {
    // Live-apply as the user tweaks — but ONLY when the toggle is on.
    setUiScale(scale);
  }, [scale]);

  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    setUiScaleEnabled(next);
  };

  const step = (delta) => {
    if (!enabled) { setEnabled(true); setUiScaleEnabled(true); }
    const next = Math.round((scale + delta) * 100) / 100;
    setScale(Math.max(UI_SCALE_MIN, Math.min(UI_SCALE_MAX, next)));
  };

  const applyBestFit = () => {
    if (!enabled) { setEnabled(true); setUiScaleEnabled(true); }
    setScale(bestFitScale());
  };

  // v1.4.47 — Smart-TV D-pad UX for the slider. Press OK to enter
  // edit mode (Left/Right adjust value), press OK again to exit.
  // Fixes the "stuck on the moving bar" issue reported when D-pad
  // was captured by the range input on Smart-TV WebViews.
  const [sliderEditing, setSliderEditing] = useState(false);
  const sliderKeyDown = (e) => {
    const key = e.key;
    if (key === "Enter" || key === " ") {
      e.preventDefault();
      e.stopPropagation();
      setSliderEditing((v) => !v);
      return;
    }
    if (!sliderEditing) {
      // Let spatial-nav handle focus movement off the slider.
      return;
    }
    if (key === "ArrowLeft")  { e.preventDefault(); e.stopPropagation(); step(-0.05); }
    else if (key === "ArrowRight") { e.preventDefault(); e.stopPropagation(); step(0.05); }
    else if (key === "ArrowUp" || key === "ArrowDown") {
      // Swallow while editing so focus doesn't escape mid-adjust.
      e.preventDefault();
      e.stopPropagation();
    } else if (key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setSliderEditing(false);
    }
  };

  const body = (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      {firstRun && (
        <div className="text-center space-y-1">
          <div className="text-xs uppercase tracking-widest text-orange-400 font-bold">
            One-time setup
          </div>
          <h1 className="text-3xl font-black text-white">
            Adjust screen size
          </h1>
          <p className="text-sm text-slate-400 max-w-lg mx-auto leading-relaxed">
            If posters look too big or too small on your TV, use the
            controls below to shrink or grow the whole interface. You can
            change this at any time from Settings later.
          </p>
        </div>
      )}

      <div className="rounded-2xl bg-[#11141b] border border-white/5 p-6 space-y-5">
        {/* v1.4.46 — Enable-first toggle. When OFF the sliders below
            are visible for reference but the app stays at 100%. */}
        <div
          data-testid="ui-scale-enable-row"
          className="flex items-center justify-between gap-4 p-3 rounded-xl bg-white/5 border border-white/5"
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">
              Use custom screen size
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
              Turn this on to shrink or grow the whole app. When off, the
              app stays at 100% regardless of the value below.
            </div>
          </div>
          <button
            data-testid="ui-scale-enable-toggle"
            role="switch"
            aria-checked={enabled}
            onClick={toggleEnabled}
            className={`relative shrink-0 w-12 h-7 rounded-full transition-colors outline-none focus:ring-4 focus:ring-orange-500 ${
              enabled ? "bg-orange-500" : "bg-white/10"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${
                enabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        {/* Best fit — auto-picks a scale for the current viewport. */}
        <button
          data-testid="ui-scale-best-fit"
          onClick={applyBestFit}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-sky-500/10 hover:bg-sky-500/20 border-2 border-sky-500/40 text-sky-200 text-sm font-semibold transition-colors outline-none focus:ring-4 focus:ring-sky-500"
        >
          <Maximize2 className="w-4 h-4" />
          Best fit to screen
        </button>

        {/* Big readout + steppers */}
        <div className={`flex items-center justify-center gap-4 ${enabled ? "" : "opacity-50"}`}>
          <button
            data-testid="ui-scale-decrease"
            onClick={() => step(-0.05)}
            className="w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white outline-none focus:ring-4 focus:ring-orange-500"
            aria-label="Smaller"
          >
            <Minus className="w-5 h-5" />
          </button>
          <div className="text-5xl font-black text-white tabular-nums w-32 text-center">
            {Math.round(scale * 100)}%
          </div>
          <button
            data-testid="ui-scale-increase"
            onClick={() => step(0.05)}
            className="w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white outline-none focus:ring-4 focus:ring-orange-500"
            aria-label="Bigger"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {/* Slider — v1.4.47 with Smart-TV D-pad edit mode. Wrapping
            div picks up the OK-to-toggle behaviour; the input still
            drives the value visually. */}
        <div
          data-testid="ui-scale-slider-wrap"
          tabIndex={0}
          role="slider"
          aria-valuemin={UI_SCALE_MIN}
          aria-valuemax={UI_SCALE_MAX}
          aria-valuenow={scale}
          aria-label="Screen size — press OK to adjust"
          onKeyDown={sliderKeyDown}
          className={`rounded-xl p-3 border-2 outline-none transition-colors ${
            sliderEditing
              ? "border-orange-500 bg-orange-500/10 ring-4 ring-orange-500/40"
              : "border-white/10 bg-white/5 focus:border-orange-400"
          }`}
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5 flex items-center justify-between">
            <span>Slider — press OK to {sliderEditing ? "save & exit" : "adjust"}</span>
            {sliderEditing && (
              <span
                data-testid="ui-scale-editing-hint"
                className="text-orange-300 font-bold animate-pulse"
              >
                EDITING · ◄ ►
              </span>
            )}
          </div>
          <input
            data-testid="ui-scale-slider"
            type="range"
            min={UI_SCALE_MIN}
            max={UI_SCALE_MAX}
            step={0.05}
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            tabIndex={-1}
            className="w-full accent-orange-500 pointer-events-none"
          />
        </div>
        <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-500 -mt-3">
          <span>Smaller ({Math.round(UI_SCALE_MIN * 100)}%)</span>
          <span>Default (100%)</span>
          <span>Bigger ({Math.round(UI_SCALE_MAX * 100)}%)</span>
        </div>

        {/* Preset chips */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.value}
              data-testid={`ui-scale-preset-${Math.round(p.value * 100)}`}
              onClick={() => setScale(p.value)}
              className={`px-3 py-2 rounded-lg border-2 text-xs font-semibold transition-colors outline-none focus:ring-4 focus:ring-orange-500 ${
                Math.abs(scale - p.value) < 0.001
                  ? "border-orange-500 bg-orange-500/15 text-orange-200"
                  : "border-white/10 bg-white/5 text-slate-300 hover:border-white/30"
              }`}
              title={p.hint}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Reset + Done */}
        <div className="flex items-center justify-between gap-3 pt-2">
          <button
            data-testid="ui-scale-reset"
            onClick={() => setScale(UI_SCALE_DEFAULT)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/5 border border-white/10 outline-none focus:ring-4 focus:ring-orange-500"
          >
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
          {onDone && (
            <button
              data-testid="ui-scale-done"
              onClick={() => {
                try { localStorage.removeItem("tv_player.firstRunScreenSize"); } catch { /* */ }
                onDone(scale);
              }}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold outline-none focus:ring-4 focus:ring-orange-500"
            >
              <Check className="w-4 h-4" />
              {firstRun ? "Continue" : "Done"}
            </button>
          )}
        </div>
      </div>

      <p className="text-center text-[11px] text-slate-500 leading-relaxed">
        Changes apply to every screen — Home, Live TV, Movies, Series and
        Search. You can re-open this at any time from{" "}
        <span className="text-slate-300">Settings → Screen size</span>.
      </p>
    </div>
  );

  if (!showFrame) return body;

  return (
    <div
      data-testid="screen-size-setup"
      className="fixed inset-0 z-[100] bg-[#07080b]/95 backdrop-blur-md overflow-y-auto p-6 flex items-center"
    >
      {body}
    </div>
  );
}
